import { useState, useMemo } from 'react';
import { Mill, StateType } from '../lib/types';
import { getAllMills } from '../lib/millsData';

// Persist the explorer's state + mill selection across navigations so
// pressing "Back" on DetailView lands users on the explorer with their
// dropdowns still populated. Without this, the route swap unmounts /
// remounts ExplorerView with fresh useState defaults — the user perceives
// "Back" as resetting two screens of work, not one.
const STATE_STORAGE_KEY = 'sfi.explorer.state';
const MILL_ID_STORAGE_KEY = 'sfi.explorer.millId';

const VALID_STATES: ReadonlySet<StateType> = new Set([
  'California',
  'Oregon',
  'Georgia',
  'Alabama',
]);

const isValidState = (raw: string | null): raw is StateType =>
  raw !== null && VALID_STATES.has(raw as StateType);

const readStoredState = (): StateType | null => {
  try {
    const raw = localStorage.getItem(STATE_STORAGE_KEY);
    return isValidState(raw) ? (raw as StateType) : null;
  } catch {
    return null;
  }
};

const readStoredMillId = (): string | null => {
  try {
    return localStorage.getItem(MILL_ID_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (state: StateType | null, millId: string | null) => {
  try {
    if (state) localStorage.setItem(STATE_STORAGE_KEY, state);
    else localStorage.removeItem(STATE_STORAGE_KEY);
    if (millId) localStorage.setItem(MILL_ID_STORAGE_KEY, millId);
    else localStorage.removeItem(MILL_ID_STORAGE_KEY);
  } catch {
    // ignore quota / privacy-mode failures — in-memory state still updates
  }
};

/**
 * Imperative write into the same localStorage slot the Explorer's mill
 * picker reads on mount. Exposed for navigators that bypass the
 * useMillSelection hook (today: ScenarioCard) so that returning to the
 * Explorer via a `fromDetail` Back lands on dropdowns that match the
 * shed the user was just viewing — instead of stale or empty selection.
 */
export const setStoredMillSelection = (mill: Mill | null): void => {
  if (mill) {
    writeStored(mill.state as StateType, mill.id);
  } else {
    writeStored(null, null);
  }
};

export const useMillSelection = () => {
  const allMills = useMemo(() => getAllMills(), []);

  // Restore on first render. Use lazy initializer so we don't run the
  // localStorage read on every render. Mill restoration validates the
  // stored id against the loaded mills list — if the id no longer
  // resolves (data file changed, stale cache), fall back to null
  // rather than crashing the panel.
  const [selectedState, setSelectedState] = useState<StateType | null>(() =>
    readStoredState(),
  );
  const [selectedMill, setSelectedMill] = useState<Mill | null>(() => {
    const storedId = readStoredMillId();
    if (!storedId) return null;
    return allMills.find((m) => m.id === storedId) ?? null;
  });

  const millsInState = useMemo(() => {
    if (!selectedState) return [];
    return allMills.filter((mill) => mill.state === selectedState);
  }, [selectedState, allMills]);

  // Persistence runs synchronously inside the setters — NOT via a
  // useEffect on selectedState/selectedMill. The user-facing flow is
  // selectMill → navigate('/detail/...') in the same event tick, and
  // React Router unmounts ExplorerView before pending effects from
  // ExplorerView's last render get to commit. An effect-driven write
  // therefore loses the just-made selection. Synchronous writes inside
  // the setters guarantee the value is in localStorage before navigate
  // pushes the new route.
  const selectState = (state: StateType | null) => {
    setSelectedState(state);
    setSelectedMill(null);
    writeStored(state, null);
  };

  const selectMill = (mill: Mill | null) => {
    if (mill) {
      const millState = mill.state as StateType;
      setSelectedState(millState);
      setSelectedMill(mill);
      writeStored(millState, mill.id);
    } else {
      setSelectedMill(null);
      // Keep the current state filter; just clear the mill id.
      writeStored(selectedState, null);
    }
  };

  return {
    allMills,
    selectedState,
    selectedMill,
    millsInState,
    selectState,
    selectMill,
  };
};
